import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, UserPlus, X } from 'lucide-react';
import {
  errorText, newRequestId, ownerBreakdownText, plural, postBulkReassign, skipSummary,
  type BulkReassignResult, type PickableEmployee, type PickedLead,
} from './bulkReassign';

/** Reassign box — new owner, follow-ups, reason, what gets skipped. */
export function BulkReassignDialog({ leads, employees, onClose, onDone }: {
  leads: PickedLead[];
  /** The same assignable list the row's "Assigned" dropdown uses (/employees?scope=assignable). */
  employees: PickableEmployee[];
  onClose: () => void;
  onDone: (result: BulkReassignResult) => void;
}) {
  const owners = useMemo(
    () => employees
      .filter((e) => !!e.userId)
      .map((e) => ({ userId: e.userId as string, label: `${e.firstName} ${e.lastName}`.trim() + (e.designation ? ` · ${e.designation}` : '') }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [employees],
  );
  const [toUserId, setToUserId] = useState('');
  const [moveFollowUps, setMoveFollowUps] = useState(true);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<BulkReassignResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(newRequestId());
  const attempted = useRef(false);
  const ids = useMemo(() => leads.map((l) => l.id), [leads]);

  useEffect(() => {
    if (!toUserId) { setPreview(null); return; }
    let live = true;
    setPreviewing(true); setError('');
    postBulkReassign({ leadIds: ids, toUserId, reason: 'preview', moveFollowUps, requestId: requestId.current, dryRun: true })
      .then((r) => { if (live) setPreview(r); })
      .catch((e) => { if (live) { setPreview(null); setError(errorText(e)); } })
      .finally(() => { if (live) setPreviewing(false); });
    return () => { live = false; };
    // moveFollowUps does not change who is skipped
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toUserId, ids]);

  const willMove = preview ? preview.ok : leads.length;
  const skips = preview ? skipSummary(preview.results) : [];
  const skippedCount = preview ? preview.skipped : 0;
  const canGo = !!toUserId && reason.trim().length > 0 && !previewing && !busy && (!preview || preview.ok > 0);

  async function go() {
    if (!canGo) return;
    attempted.current = true;
    setBusy(true); setError('');
    try {
      const r = await postBulkReassign({ leadIds: ids, toUserId, reason: reason.trim(), moveFollowUps, requestId: requestId.current, dryRun: false });
      onDone(r);
    } catch (e) {
      // Same requestId on the next click — the server finishes the batch, never doubles it.
      setError(`Could not reassign: ${errorText(e)}. Try again — nothing will be moved twice.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" onClick={() => !busy && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Reassign ${plural(leads.length, 'lead')}`}
        data-tour="leads-bulk-dialog"
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-indigo-500/30 bg-[var(--bg-surface)] p-5 shadow-2xl sm:max-w-md sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-[var(--text-primary)]">Reassign {plural(leads.length, 'lead')}</h3>
            <p className="text-xs text-[var(--text-muted)]">From {ownerBreakdownText(leads)}.</p>
          </div>
          <button type="button" aria-label="Close" onClick={() => !busy && onClose()} className="rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)]">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="bulk-owner" className="mb-1 block text-xs font-medium text-[var(--text-muted)]">New owner</label>
            <select
              id="bulk-owner"
              data-tour="leads-bulk-owner"
              value={toUserId}
              disabled={busy || attempted.current}
              onChange={(e) => { setToUserId(e.target.value); if (!attempted.current) requestId.current = newRequestId(); }}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
            >
              <option value="">Pick a person…</option>
              {owners.map((o) => <option key={o.userId} value={o.userId}>{o.label}</option>)}
            </select>
          </div>

          <label className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
            <input
              type="checkbox"
              checked={moveFollowUps}
              onChange={(e) => setMoveFollowUps(e.target.checked)}
              className="mt-0.5 rounded accent-indigo-500"
            />
            <span>Move their pending follow-ups too (dates stay the same)</span>
          </label>

          <div>
            <label htmlFor="bulk-reason" className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Reason (shown in each lead's history)</label>
            <input
              id="bulk-reason"
              data-tour="leads-bulk-reason"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. on leave this week"
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none"
            />
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2 text-xs font-medium text-orange-400" data-tour="leads-bulk-skips">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            {previewing ? (
              <span>Checking these leads…</span>
            ) : preview && skippedCount > 0 ? (
              <span>
                {plural(skippedCount, 'lead')} will be skipped: {skips.map((s) => `${s.count} ${s.label}`).join(', ')}.
              </span>
            ) : preview ? (
              <span>None of these leads will be skipped.</span>
            ) : (
              <span>Leads with a dealer, won, lost, duplicates, dealer prospects and leads you cannot see are skipped. Stages do not change.</span>
            )}
          </div>

          {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="flex-1 rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={go}
            disabled={!canGo}
            data-tour="leads-bulk-go"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {`Reassign ${plural(willMove, 'lead')}`}
          </button>
        </div>
      </div>
    </div>
  );
}
